import CenteredTooltip from '@centered/components/dist/CenteredTooltip'
import FancyAvatar from '@centered/components/dist/FancyAvatar'
import ChevronIcon from '@centered/components/dist/icons/ChevronIcon'
import Medal from '@centered/components/dist/icons/Medal'
import SpinnerIcon from '@centered/components/dist/icons/SpinnerIcon'
import { converter, db, useCurrentUser } from '@centered/firebase'
import { LeaderboardUser } from '@centered/types'
import { Listbox } from '@headlessui/react'
import { DateTime } from 'luxon'
import { useEffect, useState } from 'react'
import { LeaderboardRange } from './GroupsLeaderboard'

interface Props {
  className?: string
  groupId: string
  groupName: string
}

export default function GroupLeaderboard({
  className,
  groupId,
}: Props): JSX.Element {
  const { currentUser } = useCurrentUser()
  const [selectedTopUserMenu, setSelectedTopUserMenu] =
    useState<LeaderboardRange>('Today')
  const [loaded, setLoaded] = useState(false)
  const [userChangedSelection, setUserChangedSelection] = useState(false)
  const [topUsers, setTopUsers] = useState<LeaderboardUser[]>([])
  const [today, setToday] = useState(
    DateTime.now().setZone('America/Los_Angeles'),
  )

  // refresh today's date every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setToday(DateTime.now().setZone('America/Los_Angeles'))
    }, 60 * 1000)
    return clearInterval(interval)
  }, [])

  useEffect(() => {
    setLoaded(false)
    setTopUsers([])
    const dailyDocId = today.toFormat('yyyy-LL-dd')
    const weeklyDocId = today.toFormat(`yyyy-'W'WW`)
    const monthlyDocId = today.toFormat('yyyy-LL')
    const id =
      selectedTopUserMenu === 'All Time'
        ? 'alltime'
        : selectedTopUserMenu === 'Today'
        ? dailyDocId
        : selectedTopUserMenu === 'This Week'
        ? weeklyDocId
        : monthlyDocId

    const unsubscribe = db
      .collection(`groups/${groupId}/leaderboards`)
      .doc(id)
      .collection('users')
      .where('score', '>=', 0)
      .orderBy('score', 'desc')
      .withConverter(converter<LeaderboardUser>())
      .limit(10)
      .onSnapshot(
        (snapshot) => {
          setTopUsers(
            snapshot.docs
              .map((d) => {
                return { ...d.data(), id: d.id }
              })
              .sort((a, b) => {
                return b.score - a.score
              }),
          )
          setLoaded(true)
        },
        (error) => {
          console.error('leaderboard subscription error: ', error)
        },
      )
    return () => {
      unsubscribe()
    }
  }, [selectedTopUserMenu, groupId, today])

  // Show alltime if the current selection doesn't have anything
  useEffect(() => {
    if (loaded && topUsers.length === 0 && !userChangedSelection) {
      setSelectedTopUserMenu('All Time')
    }
  }, [topUsers, loaded, userChangedSelection])

  return (
    <div className={`w-full ${className ?? ''}`}>
      <header className="flex flex-row gap-2 items-center mb-4">
        <div className="flex flex-col">
          <CenteredTooltip
            arrow
            name="The Leaderboard starts at 12:00AM PT and ends at 11:59PM PT. Only 15m+ Sessions are counted towards it."
          >
            <h1 className="text-sm font-semibold">Leaderboard</h1>
          </CenteredTooltip>
        </div>

        <div className="flex-1 flex items-center">
          <Listbox
            value={selectedTopUserMenu}
            onChange={(newRange) => {
              setSelectedTopUserMenu(newRange)
              setUserChangedSelection(true)
            }}
          >
            <div className="relative text-xs">
              <Listbox.Button className="relative cursor-pointer rounded-lg py-2 pl-3 pr-10 text-left border border-white border-opacity-10">
                <span className="block truncate">{selectedTopUserMenu}</span>

                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronIcon direction="down" />
                </span>
              </Listbox.Button>

              <Listbox.Options className="absolute top-full z-10 mt-1 max-h-60 w-24 -ml-1 overflow-auto rounded-md bg-white text-black py-1 shadow-lg">
                {['Today', 'This Week', 'This Month', 'All Time'].map(
                  (timeRange, idx) => (
                    <Listbox.Option
                      key={idx}
                      className={() =>
                        `relative cursor-pointer select-none py-2 px-3`
                      }
                      value={timeRange}
                    >
                      {({ selected }) => (
                        <div
                          className={`truncate ${
                            selected ? 'font-semibold' : 'font-normal'
                          }`}
                        >
                          {timeRange}
                        </div>
                      )}
                    </Listbox.Option>
                  ),
                )}
              </Listbox.Options>
            </div>
          </Listbox>

          {!loaded && (
            <div className="order-1">
              <SpinnerIcon />
            </div>
          )}
        </div>

        <div className="text-xs flex-shrink-0 text-white text-opacity-50">
          Score
        </div>
      </header>

      <div className="py-2 rounded-lg space-y-3">
        {topUsers.map((user, i) => (
          <div
            key={user.id}
            className={`flex flex-row gap-2 text-sm items-center rounded${
              user.id === currentUser?.id ? 'bg-white bg-opacity-10' : ''
            }`}
            style={{ minWidth: 0 }}
          >
            <div className="w-5 flex-shrink-0 flex justify-center font-semibold">
              {i === 0 ? (
                <Medal rank={1} className="text-2xl flex-shrink-0 mt-1" />
              ) : i === 1 ? (
                <Medal rank={2} className="text-2xl flex-shrink-0 mt-1" />
              ) : i === 2 ? (
                <Medal rank={3} className="text-2xl flex-shrink-0 mt-1" />
              ) : (
                `${i + 1}`
              )}
            </div>

            <div className="overflow-hidden flex-shrink-0">
              <FancyAvatar
                name={
                  user.id === currentUser?.id ? currentUser.name : user.name
                }
                avatarUrl={
                  user.id === currentUser?.id
                    ? currentUser.avatarUrl
                    : user.avatarUrl
                }
                userId={user.id}
                // size={2}
              />
            </div>

            <p
              className={`flex-1 truncate ${
                user.id === currentUser?.id ? 'font-semibold' : ''
              }`}
            >
              {user.id === currentUser?.id ? 'You' : user.name}
            </p>

            <div
              className={`flex-shrink-0 ${
                user.id === currentUser?.id ? 'font-semibold' : ''
              }`}
            >
              {user.score > 0 ? user.score.toLocaleString('en-US') : '–'}
            </div>
          </div>
        ))}

        {loaded && topUsers.length === 0 && (
          <div className=" text-xs text-white text-opacity-50 text-center mt-20 mb-20 px-10">
            There's nobody on the Leaderboard yet.{' '}
          </div>
        )}
      </div>
    </div>
  )
}
